import { Subject } from 'rxjs';
import { APP_CONSTANTS } from '../../core/constants/app.constants';
import { normalizeRoleNameForPermissions } from '../../core/utils/role-normalization.utils';

export const CSE_NOTE_REMINDER_INTERVAL_MS = 60 * 60 * 1000;
export const CSE_NOTE_REMINDER_LOGIN_DELAY_MS = 8 * 1000;
export const CSE_NOTE_REMINDER_TOAST_LIFE_MS = 20 * 1000;
export const CSE_NOTE_REMINDER_TOAST_KEY = 'cse-note-reminder';

/** Header listens and opens the existing Add Note dialog. */
export const cseNoteReminderOpen$ = new Subject<void>();

/** Fired when the logged-in shell is on screen. */
export const cseNoteReminderCheck$ = new Subject<void>();

export function cseNoteReminderStorageKey(userId: string): string {
  return `cse-note-reminder:${userId}`;
}

export function readCseNoteReminderShownAt(userId: string): number | null {
  try {
    const raw = sessionStorage.getItem(cseNoteReminderStorageKey(userId));
    const value = Number(raw);
    return raw && Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

export function writeCseNoteReminderShownAt(userId: string): void {
  try {
    sessionStorage.setItem(cseNoteReminderStorageKey(userId), String(Date.now()));
  } catch {
    /* session storage unavailable */
  }
}

/** CSE only. CSE Team Lead and every other role stay out. */
export function isCseNoteReminderRole(role: string | null | undefined): boolean {
  return normalizeRoleNameForPermissions(role) === APP_CONSTANTS.ROLES.CSE;
}

/** True when either the stored role or the label shown in the header is CSE. */
export function isCseNoteReminderUser(
  roleName: string | null | undefined,
  roleDisplayName?: string | null
): boolean {
  return isCseNoteReminderRole(roleName) || isCseNoteReminderRole(roleDisplayName);
}

/** First visit and an overdue refresh use the short delay. Otherwise wait out the interval. */
export function msUntilCseNoteReminder(
  lastShownAt: number | null,
  now: number,
  loginDelayMs = CSE_NOTE_REMINDER_LOGIN_DELAY_MS,
  intervalMs = CSE_NOTE_REMINDER_INTERVAL_MS
): number {
  if (lastShownAt == null || !Number.isFinite(lastShownAt)) {
    return loginDelayMs;
  }
  const remaining = lastShownAt + intervalMs - now;
  return remaining > 0 ? remaining : loginDelayMs;
}
